import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import { 
  MessageSquare, 
  Send, 
  Mail,
  MailOpen,
  Clock,
  User,
  Reply,
  CheckCircle,
  Inbox
} from "lucide-react";

export default function TenantMessagesPage() {
  const { token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyContent, setReplyContent] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Failed to load messages");
    } finally {
      setLoading(false);
    }
  };

  const openMessage = async (message) => {
    setSelectedMessage(message);
    setReplyContent("");
    setViewOpen(true);
    
    // Mark as read if not already
    if (!message.is_read) {
      try {
        await axios.post(`${API}/messages/${message.id}/read`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        // Update local state
        setMessages(prev => prev.map(m => 
          m.id === message.id ? {...m, is_read: true} : m
        ));
      } catch (error) {
        console.error("Failed to mark message as read:", error);
      }
    }
  };

  const handleReply = async () => {
    if (!replyContent.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    setSending(true);
    try {
      await axios.post(`${API}/messages/${selectedMessage.id}/reply`, 
        { content: replyContent },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success("Reply sent successfully!");
      setReplyContent("");
      
      // Refresh messages to get updated data
      const response = await axios.get(`${API}/messages/${selectedMessage.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedMessage(response.data);
      fetchMessages();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = messages.filter(m => !m.is_read).length;

  return (
    <div className="flex min-h-screen bg-background" data-testid="tenant-messages-page">
      <Sidebar />
      
      <main className="flex-1 ml-0 md:ml-64 p-4 pt-20 md:pt-6 md:p-6">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-primary">My Messages</h1>
            <p className="text-text-muted mt-1">
              View messages from the administrator
              {unreadCount > 0 && (
                <Badge className="ml-2 bg-red-500 text-white">
                  {unreadCount} unread
                </Badge>
              )}
            </p>
          </div>

          {/* Messages List */}
          <Card className="border-stone-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Inbox className="w-5 h-5" />
                Inbox ({messages.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <div className="animate-pulse text-primary">Loading...</div>
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageSquare className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <p className="text-text-muted">No messages yet</p>
                  <p className="text-sm text-stone-400 mt-2">
                    Messages from the administrator will appear here
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg) => (
                    <div 
                      key={msg.id}
                      onClick={() => openMessage(msg)}
                      className={`rounded-xl p-4 border cursor-pointer transition-all hover:shadow-md ${
                        msg.is_read 
                          ? 'bg-stone-50 border-stone-200' 
                          : 'bg-blue-50 border-blue-200'
                      }`}
                      data-testid={`message-card-${msg.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                          msg.is_read ? 'bg-stone-200' : 'bg-blue-500'
                        }`}>
                          {msg.is_read ? (
                            <MailOpen className="w-5 h-5 text-stone-500" />
                          ) : (
                            <Mail className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-primary">
                              From: {msg.sender_name}
                            </span>
                            {!msg.is_read && (
                              <Badge className="bg-blue-500 text-white text-xs">New</Badge>
                            )}
                            {msg.replies?.length > 0 && (
                              <Badge className="bg-green-100 text-green-800 text-xs">
                                {msg.replies.length} {msg.replies.length === 1 ? 'Reply' : 'Replies'}
                              </Badge>
                            )}
                          </div>
                          <h3 className={`font-medium truncate ${
                            msg.is_read ? 'text-stone-700' : 'text-stone-900'
                          }`}>
                            {msg.subject}
                          </h3>
                          <p className="text-sm text-text-muted truncate mt-1">{msg.content}</p>
                          <div className="flex items-center gap-2 mt-2 text-xs text-stone-400">
                            <Clock className="w-3 h-3" />
                            {formatDate(msg.created_at)}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>

      {/* View Message Dialog */}
      <Dialog open={viewOpen} onOpenChange={setViewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Message
            </DialogTitle>
          </DialogHeader>
          
          {selectedMessage && (
            <div className="space-y-4 py-4">
              {/* Original Message */}
              <div className="bg-stone-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                    <User className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-semibold">{selectedMessage.sender_name}</span>
                  <Badge className="bg-primary/10 text-primary text-xs">Admin</Badge>
                  <span className="text-xs text-stone-400 ml-auto">
                    {formatDate(selectedMessage.created_at)}
                  </span>
                </div>
                <h3 className="text-lg font-semibold mb-2">{selectedMessage.subject}</h3>
                <p className="text-stone-600 whitespace-pre-wrap">{selectedMessage.content}</p>
              </div>
              
              {/* Replies Section */}
              {selectedMessage.replies?.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-sm text-stone-500">
                    <Reply className="w-4 h-4" />
                    Conversation ({selectedMessage.replies.length})
                  </h4>
                  {selectedMessage.replies.map((reply) => (
                    <div 
                      key={reply.id}
                      className={`rounded-xl p-4 ${
                        reply.sender_role === 'tenant'
                          ? 'bg-green-50 border-l-4 border-green-500 ml-8'
                          : 'bg-stone-50 border-l-4 border-primary'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">
                          {reply.sender_name}
                        </span>
                        <Badge className={`text-xs ${
                          reply.sender_role === 'tenant'
                            ? 'bg-green-200 text-green-900'
                            : 'bg-primary/10 text-primary'
                        }`}>
                          {reply.sender_role === 'tenant' ? 'You' : 'Admin'}
                        </Badge>
                        <span className="text-xs text-stone-400 ml-auto">
                          {formatDate(reply.created_at)}
                        </span>
                      </div>
                      <p className="text-stone-700 whitespace-pre-wrap">{reply.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Reply Section */}
              <div className="border-t border-stone-200 pt-4">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Reply className="w-4 h-4" />
                  Reply
                </h4>
                <Textarea
                  data-testid="reply-content-input"
                  placeholder="Write your reply here..."
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  rows={4}
                  className="mb-3"
                />
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setViewOpen(false)}>
                    Close
                  </Button>
                  <Button 
                    onClick={handleReply}
                    disabled={sending || !replyContent.trim()}
                    className="bg-primary hover:bg-primary-800"
                    data-testid="send-reply-btn"
                  >
                    {sending ? "Sending..." : "Send Reply"}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
